/**
 * Tecnologia da Informação, Fase 1 (ADR 0034): catálogo de softwares, contratações e assinaturas
 * SaaS, direitos de licença (lotes) e atribuições. Seis conceitos separados: fornecedor (cadastro
 * de parceiros) → software → plano comercial → contratação → direito de licença → atribuição.
 * O rateio por competência vem na fase financeira; aqui ficam os dados que ele vai usar.
 */
import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';
import { HUNDRED_PERCENT, percentageTotal } from './people.ts';
import { localizedText } from './schemas.ts';

export const IT_SOFTWARE_DELIVERY = ['installed', 'saas', 'hybrid'] as const;
export const IT_PLATFORMS = ['windows', 'macos', 'linux', 'web', 'ios', 'android'] as const;
/** Uso na empresa: homologado, restrito (precisa de aprovação), proibido ou em avaliação. */
export const IT_SOFTWARE_CLASSIFICATIONS = [
  'approved',
  'restricted',
  'prohibited',
  'under_review',
] as const;
export const IT_SOFTWARE_STATUSES = ['active', 'evaluation', 'discontinued'] as const;
/** Modalidades de licenciamento (seção 7.2 do pedido). */
export const IT_LICENSE_MODELS = [
  'per_user',
  'per_device',
  'concurrent',
  'perpetual',
  'subscription',
  'consumption',
  'capacity',
  'enterprise',
  'bundle',
] as const;
export const IT_BILLING_PERIODS = [
  'monthly',
  'quarterly',
  'semiannual',
  'annual',
  'biennial',
  'triennial',
  'one_time',
  'usage',
] as const;
export type ItBillingPeriod = (typeof IT_BILLING_PERIODS)[number];
/** Meses de cada período de cobrança (avulso e por consumo não têm equivalente mensal). */
export const IT_BILLING_PERIOD_MONTHS: Record<ItBillingPeriod, number | null> = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
  biennial: 24,
  triennial: 36,
  one_time: null,
  usage: null,
};
/** Só a forma de pagamento e uma referência; nunca dados do cartão ou da conta. */
export const IT_PAYMENT_METHODS = [
  'corporate_card',
  'bank_slip',
  'bank_transfer',
  'invoice',
  'marketplace',
  'other',
] as const;
export const IT_SUBSCRIPTION_STATUSES = ['active', 'suspended', 'cancelled'] as const;
/** Situação exibida: a gravada, mais "vencida" e "cancelamento agendado", pelas datas. */
export const IT_EFFECTIVE_STATUSES = [
  'active',
  'suspended',
  'cancel_scheduled',
  'cancelled',
  'expired',
] as const;
export type ItEffectiveStatus = (typeof IT_EFFECTIVE_STATUSES)[number];
/** Lote de licenças (ocupa vagas) ou de valor fixo da contratação (quantidade 1, não ocupa vagas). */
export const IT_ENTITLEMENT_KINDS = ['seats', 'fee'] as const;
export const IT_ENTITLEMENT_ORIGINS = ['initial', 'addition', 'renewal', 'price_change'] as const;
export const IT_ASSIGNMENT_STATUSES = ['active', 'suspended', 'ended'] as const;
export const IT_END_REASONS = [
  'recovered',
  'transferred',
  'user_inactive',
  'subscription_cancelled',
  'other',
] as const;
/** Motivos que a pessoa escolhe ao recuperar (os outros o sistema registra sozinho). */
export const IT_RECOVER_REASONS = ['recovered', 'user_inactive', 'other'] as const;
export const IT_ATTACHMENT_KINDS = ['contract', 'proposal', 'invoice', 'terms', 'other'] as const;
export const IT_ALERT_SOURCES = ['renewal', 'notice', 'entitlement_end'] as const;
export type ItAlertSource = (typeof IT_ALERT_SOURCES)[number];
/** Marcos dos avisos (dias antes do prazo); 0 = venceu. */
export const IT_ALERT_MILESTONES = [60, 30, 7] as const;
/** Janela do painel para "próximos do vencimento" (dias). */
export const IT_DASHBOARD_WINDOW_DAYS = 60;

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const day = z
  .string()
  .regex(/^(\d{4}-\d{2}-\d{2})?$/, 'Data no formato AAAA-MM-DD')
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const requiredDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe a data');
const amount = z
  .string()
  .trim()
  // Valor negativo tem mensagem própria (B12); o formato continua com ponto.
  .refine((v) => !v.startsWith('-'), 'O valor não pode ser negativo')
  .refine(
    (v) => v.startsWith('-') || /^\d{1,15}(\.\d{1,4})?$/.test(v),
    'Use números com ponto (ex.: 1500.00)',
  );
const website = z
  .string()
  .trim()
  .max(300)
  .regex(/^(https?:\/\/\S+)?$/, 'Endereço começando com http:// ou https://')
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const reason = z.string().trim().min(3, 'Explique o motivo').max(2000);
/** Sequências longas de dígitos parecem número de cartão ou de conta: recusadas (como no banco). */
const PAYMENT_NOTE_DIGITS = /[0-9][0-9 .-]{6,}[0-9]/;

export const itCategoryInput = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z][A-Z0-9_-]{1,29}$/, 'Use de 2 a 30 letras maiúsculas, números, - ou _'),
  name: localizedText,
  sortOrder: z.number().int().min(1).max(999).default(100),
  isActive: z.boolean().default(true),
});
export type ItCategoryInput = z.infer<typeof itCategoryInput>;

export const itManufacturerInput = z.object({
  name: z.string().trim().min(1).max(200),
  website,
  partnerId: id,
  notes: text(2000),
  isActive: z.boolean().default(true),
});
export type ItManufacturerInput = z.infer<typeof itManufacturerInput>;

export const itSoftwareInput = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9][A-Z0-9_.-]{1,29}$/, 'Use de 2 a 30 letras maiúsculas, números, ., - ou _'),
  name: z.string().trim().min(1).max(200),
  manufacturerId: z.uuid(),
  categoryId: z.uuid(),
  delivery: z.enum(IT_SOFTWARE_DELIVERY),
  platforms: z
    .array(z.enum(IT_PLATFORMS))
    .max(IT_PLATFORMS.length)
    .default([])
    .transform((v) => [...new Set(v)]),
  version: text(50),
  description: text(4000),
  website,
  classification: z.enum(IT_SOFTWARE_CLASSIFICATIONS).default('approved'),
  status: z.enum(IT_SOFTWARE_STATUSES).default('active'),
  // Entra na lista de desligamento: o checklist de quem sai traz o acesso a retirar (0292).
  offboardingRevoke: z.boolean().default(false),
});
export type ItSoftwareInput = z.infer<typeof itSoftwareInput>;

export const itPlanInput = z.object({
  softwareId: z.uuid(),
  name: z.string().trim().min(1).max(150),
  licenseModel: z.enum(IT_LICENSE_MODELS),
  billingPeriod: z.enum(IT_BILLING_PERIODS),
  description: text(2000),
  isActive: z.boolean().default(true),
});
export type ItPlanInput = z.infer<typeof itPlanInput>;

/** Como o custo da licença se divide: pelos centros de quem usa ou pelo rateio (ADR 0094). */
export const IT_COST_BASES = ['person', 'center'] as const;
export type ItCostBasis = (typeof IT_COST_BASES)[number];

/** Dados da contratação que podem ser alterados depois (com registro no histórico). */
const subscriptionTerms = z.object({
  branchId: id,
  partnerId: id,
  billingPeriod: z.enum(IT_BILLING_PERIODS),
  endDate: day,
  renewalDate: day,
  nextBillingDate: day,
  autoRenewal: z.boolean().default(false),
  noticeDays: z.number().int().min(0).max(365).nullable().optional(),
  paymentMethod: z
    .enum(IT_PAYMENT_METHODS)
    .or(z.literal(''))
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  paymentNote: text(200).refine((v) => !v || !PAYMENT_NOTE_DIGITS.test(v), {
    message:
      'Não informe número de cartão ou de conta: use só uma referência (ex.: "cartão do Marketing, final 1234")',
  }),
  ownerUserId: id,
  departmentId: id,
  costCenterId: id,
  contractId: id,
  purchaseRequestId: id,
  /** Custo por pessoa ou por centro (ADR 0094). Vazio: a conta de antes, ou o que vier da compra. */
  costBasis: z
    .enum(IT_COST_BASES)
    .or(z.literal(''))
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  overageAllowed: z.boolean().default(false),
  overageLimit: z.number().int().min(1).max(1_000_000).nullable().optional(),
  notes: text(4000),
});

const overageRule = (v: { overageAllowed: boolean; overageLimit?: number | null }) =>
  v.overageAllowed || v.overageLimit == null;

export const itSubscriptionInput = subscriptionTerms
  .extend({
    legalEntityId: z.uuid(),
    softwareId: z.uuid(),
    planId: z.uuid(),
    currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda com 3 letras (ex.: BRL)'),
    contractedOn: requiredDay,
    startDate: requiredDay,
    // Valor fixo por período (taxa da plataforma): vira um lote do tipo 'fee'; reajuste por lote.
    fixedAmount: amount.default('0'),
    // Primeiro lote de licenças (vazio para contratações sem controle por quantidade).
    initialQuantity: z.number().int().min(1).max(1_000_000).nullable().optional(),
    unitAmount: amount.nullable().optional(),
  })
  .refine(overageRule, {
    path: ['overageLimit'],
    message: 'Limite de excedente só quando o excedente é permitido',
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'O fim não pode ser antes do início',
  })
  .refine((v) => !v.renewalDate || v.renewalDate >= v.startDate, {
    path: ['renewalDate'],
    message: 'A renovação não pode ser antes do início',
  })
  .refine((v) => (v.initialQuantity == null) === (v.unitAmount == null), {
    path: ['unitAmount'],
    message: 'Informe a quantidade e o valor por licença juntos',
  });
export type ItSubscriptionInput = z.infer<typeof itSubscriptionInput>;

export const itSubscriptionUpdateInput = subscriptionTerms
  .extend({ note: text(2000) })
  .refine(overageRule, {
    path: ['overageLimit'],
    message: 'Limite de excedente só quando o excedente é permitido',
  });
export type ItSubscriptionUpdateInput = z.infer<typeof itSubscriptionUpdateInput>;

/** Novo lote de licenças (aumento de quantidade ou nova condição de preço). */
export const itEntitlementInput = z
  .object({
    kind: z.enum(IT_ENTITLEMENT_KINDS).default('seats'),
    quantity: z.number().int().min(1).max(1_000_000),
    unitAmount: amount,
    validFrom: requiredDay,
    validUntil: day,
    note: text(2000),
  })
  .refine((v) => !v.validUntil || v.validUntil >= v.validFrom, {
    path: ['validUntil'],
    message: 'O fim não pode ser antes do início',
  })
  .refine((v) => v.kind === 'seats' || v.quantity === 1, {
    path: ['quantity'],
    message: 'O valor fixo tem quantidade 1',
  });
export type ItEntitlementInput = z.infer<typeof itEntitlementInput>;

/** Encerrar a vigência de um lote (redução de quantidade); nunca no passado. */
export const itEntitlementEndInput = z.object({ validUntil: requiredDay, note: reason });
/** Cancelar um lote lançado por engano (deixa de contar). */
export const itEntitlementCancelInput = z.object({ note: reason });

export const itRenewInput = z.object({
  endDate: day,
  renewalDate: day,
  nextBillingDate: day,
  // Novo valor por licença: encerra os lotes atuais no fim do período e abre lotes novos.
  unitAmount: amount.nullable().optional(),
  // Novo valor fixo: o mesmo, com o lote de valor fixo.
  fixedAmount: amount.nullable().optional(),
  note: text(2000),
});
export type ItRenewInput = z.infer<typeof itRenewInput>;

export const itCancelInput = z.object({ effectiveOn: requiredDay, reason });
export const itSuspendInput = z.object({ reason });

/**
 * Para quem vai a licença: um colaborador (com ou sem login, ADR 0085) ou um login, um equipamento
 * (Fase 2) e/ou um departamento ou centro de custo.
 */
const assignmentTarget = z.object({
  employeeId: id,
  userId: id,
  assetId: id,
  departmentId: id,
  costCenterId: id,
  note: text(2000),
});
const hasTarget = (v: z.infer<typeof assignmentTarget>) =>
  !!(v.employeeId || v.userId || v.assetId || v.departmentId || v.costCenterId);
const targetMessage = {
  path: ['employeeId'],
  message: 'Escolha a pessoa, o equipamento, o departamento ou o centro de custo',
};

export const itAssignInput = assignmentTarget
  .extend({ startsOn: day })
  .refine(hasTarget, targetMessage);
export type ItAssignInput = z.infer<typeof itAssignInput>;
export const itTransferInput = assignmentTarget.refine(hasTarget, targetMessage);
export type ItTransferInput = z.infer<typeof itTransferInput>;
export const itRecoverInput = z
  .object({ reason: z.enum(IT_RECOVER_REASONS), note: text(2000) })
  .refine((v) => v.reason !== 'other' || !!v.note, {
    path: ['note'],
    message: 'Explique o motivo',
  });
export const itAssignmentSuspendInput = z.object({ note: reason });

export const itAttachmentInput = z.object({
  kind: z.enum(IT_ATTACHMENT_KINDS).default('other'),
  title: text(200),
});

// ---------------------------------------------------------------------------
// Cálculos (os mesmos no servidor e nas telas)
// ---------------------------------------------------------------------------

export interface ItSeats {
  contracted: number;
  assigned: number;
  suspended: number;
  overageAllowed: boolean;
  overageLimit: number | null;
}

/**
 * Vagas: disponível = contratado − atribuído − suspenso (a suspensa continua ocupando a vaga);
 * excedente = o que passa do contratado; limite = até onde se pode atribuir (null: sem limite).
 * Atribuída não quer dizer usada: o uso real só com dados de uso confiáveis (fase de integrações).
 */
export function itSeatSummary(s: ItSeats) {
  const used = s.assigned + s.suspended;
  const limit = s.overageAllowed
    ? s.overageLimit == null
      ? null
      : s.contracted + s.overageLimit
    : s.contracted;
  return {
    used,
    available: Math.max(0, s.contracted - used),
    excess: Math.max(0, used - s.contracted),
    limit,
    canAssign: limit === null || used < limit,
  };
}

/** Prazo para cancelar ou não renovar: a renovação (ou o fim) menos a antecedência. */
export function itNoticeDeadline(
  renewalDate: string | null,
  endDate: string | null,
  noticeDays: number | null,
): string | null {
  const base = renewalDate ?? endDate;
  if (!base || noticeDays == null) return null;
  const d = new Date(`${base}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - noticeDays);
  return d.toISOString().slice(0, 10);
}

/** Valor por período: valor fixo + soma de quantidade × valor por licença dos lotes. */
export function itPeriodAmount(
  fixedAmount: string,
  lots: readonly { quantity: number; unitAmount: string }[],
): string {
  return fromScaled(
    lots.reduce(
      (sum, l) => sum + BigInt(l.quantity) * toScaled(l.unitAmount),
      toScaled(fixedAmount),
    ),
  );
}

/**
 * Equivalente mensal de um valor por período (para comparar contratações de periodicidades
 * diferentes nos indicadores; não é lançamento financeiro). Arredonda a 4 casas, metade para cima.
 */
export function itMonthlyEquivalent(amount: string, period: ItBillingPeriod): string | null {
  const months = IT_BILLING_PERIOD_MONTHS[period];
  if (!months) return null;
  const scaled = toScaled(amount);
  const m = BigInt(months);
  return fromScaled((scaled * 2n + m) / (2n * m));
}

/** Situação exibida da contratação na data. */
export function itEffectiveStatus(
  s: { status: string; endDate: string | null; cancelEffectiveOn: string | null },
  today: string,
): ItEffectiveStatus {
  if (s.status === 'cancelled') {
    return s.cancelEffectiveOn && s.cancelEffectiveOn >= today ? 'cancel_scheduled' : 'cancelled';
  }
  if (s.endDate && s.endDate < today) return 'expired';
  return s.status === 'suspended' ? 'suspended' : 'active';
}

/** Soma por moeda, sem converter (a conversão por cotação datada vem na fase financeira). */
export function itTotalsByCurrency(values: readonly { amount: string; currency: string }[]) {
  const totals = new Map<string, bigint>();
  for (const v of values) {
    totals.set(v.currency, (totals.get(v.currency) ?? 0n) + toScaled(v.amount));
  }
  return [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, total]) => ({ currency, amount: fromScaled(total) }));
}

/**
 * Quem absorve a licença (B0.4): "default" volta à regra padrão (seguir a pessoa ou o equipamento);
 * "fixed" fixa um ou mais centros com percentuais que somam 100%. Vale a partir da data; a regra
 * anterior é fechada na véspera e continua no histórico.
 */
export const IT_CHARGE_MODES = ['default', 'fixed'] as const;
export const itChargeInput = z
  .object({
    mode: z.enum(IT_CHARGE_MODES),
    validFrom: z.iso.date(),
    reason: z.string().trim().min(3, 'Explique a mudança').max(2000),
    lines: z
      .array(
        z.object({
          costCenterId: z.uuid(),
          percentage: z
            .union([z.number(), z.string().trim()])
            .transform((v) => String(v).replace(',', '.'))
            .refine((v) => /^\d{1,3}(\.\d{1,4})?$/.test(v) && Number(v) > 0 && Number(v) <= 100, {
              message: 'Percentual entre 0 e 100, com até 4 casas',
            }),
        }),
      )
      .max(30)
      .default([]),
  })
  .refine((v) => v.mode === 'default' || v.lines.length > 0, {
    path: ['lines'],
    message: 'Informe pelo menos um centro de custo',
  })
  .refine((v) => v.mode === 'fixed' || v.lines.length === 0, {
    path: ['lines'],
    message: 'A regra padrão não tem centros próprios',
  })
  .refine((v) => new Set(v.lines.map((l) => l.costCenterId)).size === v.lines.length, {
    path: ['lines'],
    message: 'Cada centro de custo aparece uma vez só',
  })
  .refine(
    (v) =>
      v.mode === 'default' || percentageTotal(v.lines.map((l) => l.percentage)) === HUNDRED_PERCENT,
    { path: ['lines'], message: 'Os percentuais precisam somar 100%' },
  );
export type ItChargeInput = z.infer<typeof itChargeInput>;
